/**
 * Number and date formatting, Indian conventions throughout:
 * lakh/crore digit grouping (1,23,45,678), ₹, and IST dates.
 *
 * Every helper takes null/undefined/NaN and returns DASH, so pages can pass
 * API values straight through.
 */
import { format as formatDateFns, formatDistanceToNowStrict, parseISO } from "date-fns";

export const DASH = "—";
const LAKH = 1e5;
const CRORE = 1e7;

type Num = number | null | undefined;

function ok(n: Num): n is number {
  return typeof n === "number" && Number.isFinite(n);
}

const nf = new Map<string, Intl.NumberFormat>();
function fmt(min: number, max: number): Intl.NumberFormat {
  const key = `${min}-${max}`;
  let f = nf.get(key);
  if (!f) {
    f = new Intl.NumberFormat("en-IN", { minimumFractionDigits: min, maximumFractionDigits: max });
    nf.set(key, f);
  }
  return f;
}

/** 1234567.891 → "12,34,567.89" */
export function formatNumber(n: Num, decimals = 2, minDecimals = decimals): string {
  return ok(n) ? fmt(minDecimals, decimals).format(n) : DASH;
}

/** Whole numbers with Indian grouping: 310226 → "3,10,226" */
export function formatInt(n: Num): string {
  return ok(n) ? fmt(0, 0).format(Math.round(n)) : DASH;
}

/** Rupees: 1234.5 → "₹1,234.50" */
export function formatINR(n: Num, decimals = 2): string {
  if (!ok(n)) return DASH;
  const sign = n < 0 ? "−" : "";
  return `${sign}₹${fmt(decimals, decimals).format(Math.abs(n))}`;
}

/**
 * Rupees in compact Indian units: 2.5e7 → "₹2.50 Cr", 4.2e5 → "₹4.20 L".
 * Below a lakh the plain amount is shown.
 */
export function formatINRCompact(rupees: Num, decimals = 2): string {
  if (!ok(rupees)) return DASH;
  const a = Math.abs(rupees);
  const sign = rupees < 0 ? "−" : "";
  if (a >= CRORE) return `${sign}₹${fmt(decimals, decimals).format(a / CRORE)} Cr`;
  if (a >= LAKH) return `${sign}₹${fmt(decimals, decimals).format(a / LAKH)} L`;
  return `${sign}₹${fmt(0, 0).format(a)}`;
}

/** An amount already in ₹ crore (fundamentals, FII/DII flows): 1148.64 → "₹1,148.64 Cr" */
export function formatCrore(crore: Num, decimals = 2, { sign = false } = {}): string {
  if (!ok(crore)) return DASH;
  const s = crore < 0 ? "−" : sign && crore > 0 ? "+" : "";
  return `${s}₹${fmt(decimals, decimals).format(Math.abs(crore))} Cr`;
}

/** Counts in lakh/crore words: 31,02,26,000 → "31.02 Cr", 3,10,226 → "3.10 L" */
export function formatCompact(n: Num, decimals = 2): string {
  if (!ok(n)) return DASH;
  const a = Math.abs(n);
  const sign = n < 0 ? "−" : "";
  if (a >= CRORE) return `${sign}${fmt(decimals, decimals).format(a / CRORE)} Cr`;
  if (a >= LAKH) return `${sign}${fmt(decimals, decimals).format(a / LAKH)} L`;
  return `${sign}${fmt(0, 0).format(a)}`;
}

/** Percent, value already in percent: 2.876 → "+2.88%" (sign on by default). */
export function formatPct(n: Num, decimals = 2, { sign = true } = {}): string {
  if (!ok(n)) return DASH;
  const s = n > 0 && sign ? "+" : n < 0 ? "−" : "";
  return `${s}${fmt(decimals, decimals).format(Math.abs(n))}%`;
}

/** Signed change: -77.6 → "−77.60" */
export function formatChange(n: Num, decimals = 2): string {
  if (!ok(n)) return DASH;
  const s = n > 0 ? "+" : n < 0 ? "−" : "";
  return `${s}${fmt(decimals, decimals).format(Math.abs(n))}`;
}

export type Trend = "up" | "down" | "flat";

export function trendOf(n: Num, epsilon = 0): Trend {
  if (!ok(n) || Math.abs(n) <= epsilon) return "flat";
  return n > 0 ? "up" : "down";
}

/** Tailwind text colour for a change. */
export function trendClass(n: Num): string {
  return { up: "text-up", down: "text-down", flat: "text-muted" }[trendOf(n)];
}

function toDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  const d = typeof value === "string" ? parseISO(value) : value;
  return Number.isNaN(d.getTime()) ? null : d;
}

/** "2026-10-21" → "21 Oct 2026" */
export function formatDate(value: string | Date | null | undefined, pattern = "d MMM yyyy"): string {
  const d = toDate(value);
  return d ? formatDateFns(d, pattern) : DASH;
}

const istFormat = new Intl.DateTimeFormat("en-IN", {
  timeZone: "Asia/Kolkata",
  day: "numeric",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

/** A UTC timestamp shown in IST: "21 Oct, 21:28 IST" */
export function formatDateTimeIST(value: string | Date | null | undefined): string {
  const d = toDate(value);
  return d ? `${istFormat.format(d)} IST` : DASH;
}

/** "3 hours ago" */
export function formatAgo(value: string | Date | null | undefined): string {
  const d = toDate(value);
  return d ? `${formatDistanceToNowStrict(d)} ago` : DASH;
}

/** 3725 → "1h 2m", 42 → "42s" */
export function formatDuration(seconds: Num): string {
  if (!ok(seconds)) return DASH;
  const s = Math.round(seconds);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}
